import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Type,
  IconPlusAlt,
  Image,
  IconMore,
  PopoverTrigger,
  PopoverNavigation,
  PopoverNavigationItem,
  PopoverNavigationLink,
  Backdrop,
  DialogAlert,
  ButtonTertiary,
} from '@spotify-internal/encore-creator-web';
import {
  black,
  azure,
  spacer8,
  spacer12,
  spacer16,
  spacer20,
  spacer48,
  red,
} from '@spotify-internal/encore-foundation';
import { deleteTrailer } from '../../../lib/Api/trailerSelection';
import { Trailer } from '../../../types';
import { useMe } from '../../../hooks/useMe';

type Props = {
  toggleTrailerSelectionModal: () => void;
  updateSelectedTrailer: (trailer: Trailer | null) => void;
  trailer?: Trailer;
  podcastId: string;
};

export function PodcastInfo(props: Props) {
  const me = useMe()!;

  const { trailer, podcastId, updateSelectedTrailer, toggleTrailerSelectionModal } = props;

  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [showTrailerRemovalModal, setShowTrailerRemovalModal] = useState<boolean>(false);
  const [showTrailerRemovalError, setShowTrailerRemovalError] = useState<boolean>(false);

  const handleDeleteTrailer = async () => {
    try {
      await deleteTrailer(podcastId, trailer!.id);
      updateSelectedTrailer(null);
      setShowTrailerRemovalModal(false);
    } catch {
      setShowTrailerRemovalError(true);
    }
  };

  return (
    <PodcastInfoContainer>
      {showTrailerRemovalModal && (
        <Backdrop center>
          <DialogAlert
            body={
              <>
                <Type as="p" variant={Type.heading3}>
                  Remove trailer?
                </Type>
                <Type as="p" variant={Type.body2}>
                  This trailer will be removed from your podcast header on Spotify. The trailer will
                  remain in your list of episodes. You will also be able to select a new trailer.{' '}
                </Type>
                {showTrailerRemovalError && (
                  <Type as="p" variant={Type.body2} color="red">
                    There was an error removing your trailer. Please try again in a few minutes.
                  </Type>
                )}
              </>
            }
            footer={
              <div>
                <ButtonTertiary
                  buttonSize="sm"
                  condensed
                  onClick={() => setShowTrailerRemovalModal(false)}
                >
                  Cancel
                </ButtonTertiary>
                <ButtonTertiary
                  buttonSize="sm"
                  style={{ color: red }}
                  condensed
                  onClick={() => {
                    handleDeleteTrailer();
                  }}
                >
                  Remove
                </ButtonTertiary>
              </div>
            }
          />
        </Backdrop>
      )}
      <Type as="p" variant={Type.heading3}>
        Podcast information
      </Type>
      <Type as="p" variant={Type.body2} weight={Type.bold}>
        Podcast trailer
      </Type>
      {trailer ? (
        <>
          <TrailerWrapper>
            <TrailerCard>
              <PodcastCover src={trailer.artworkUrl} alt={trailer.name} />
              <Type as="span" variant={Type.body2} weight={Type.bold}>
                {trailer.name}
              </Type>
            </TrailerCard>
            <PopoverTrigger
              placement={PopoverTrigger.bottom}
              onShow={() => setShowMenu(true)}
              onHide={() => setShowMenu(false)}
              overlay={
                showMenu && (
                  <PopoverNavigation arrow={PopoverNavigation.top} style={{ minWidth: '120px' }}>
                    <PopoverNavigationItem>
                      <PopoverNavigationLink
                        component="button"
                        onClick={() => {
                          setShowMenu(false);
                          toggleTrailerSelectionModal();
                        }}
                        style={{ color: black, textAlign: 'center', fontWeight: 700 }}
                      >
                        Manage trailer
                      </PopoverNavigationLink>
                      <PopoverNavigationLink
                        component="button"
                        onClick={() => {
                          setShowMenu(false);
                          setShowTrailerRemovalModal(true);
                        }}
                        style={{ color: red, textAlign: 'center', fontWeight: 700 }}
                      >
                        Remove trailer
                      </PopoverNavigationLink>
                    </PopoverNavigationItem>
                  </PopoverNavigation>
                )
              }
            >
              <IconMore aria-label="Show more actions" />
            </PopoverTrigger>
          </TrailerWrapper>
        </>
      ) : (
        <TrailerSelector onClick={toggleTrailerSelectionModal}>
          <IconPlusAlt
            aria-label="IconPlusAlt"
            color={azure}
            size={12}
            style={{ marginRight: spacer8 }}
          />
          <Type as="p" variant={Type.body1} color={azure} condensed={true}>
            Select your trailer
          </Type>
        </TrailerSelector>
      )}
    </PodcastInfoContainer>
  );
}

const PodcastInfoContainer = styled.div`
  margin: ${spacer20} 0 ${spacer48};
`;

const TrailerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  padding: ${spacer12} ${spacer16};
  max-width: 400px;
`;

const TrailerCard = styled.div`
  display: flex;
  align-items: center;
`;

const PodcastCover = styled(Image).attrs<{
  src: string;
  alt: string;
}>(props => ({
  src: props.src,
  alt: props.alt,
  imageHeight: '48px',
  imageWidth: '48px',
  crop: true,
}))`
  margin-right: ${spacer12};
`;

const TrailerSelector = styled.div`
  display: flex;
  cursor: default;
`;
